import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const API_BASE_URL = 'https://api.goldfroeschli-kinderwelt.ch/api';

const COLORS = ['#2fa3d8', '#b83e31', '#4caf50', '#ff9800', '#9c27b0'];

const getLocalDateString = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  
  // Filters
  const [period, setPeriod] = useState('all'); // day, week, month, year, all, custom
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('day');

  useEffect(() => {
    // Set default dates based on period
    // IMPORTANT: Include future dates since bookings are for future events!
    const today = new Date();
    const futureEnd = new Date();
    futureEnd.setMonth(futureEnd.getMonth() + 3); // 3 months into future by default
    const end = getLocalDateString(futureEnd);
    let start;
    let endDateToSet = end; // Default end date


    switch(period) {
      case 'day':
        start = getLocalDateString(today);
        endDateToSet = getLocalDateString(today); // Today only, not future
        setGroupBy('day');
        break;
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        start = getLocalDateString(weekAgo);
        endDateToSet = getLocalDateString(today); // End at today, not future
        setGroupBy('day');
        break;
      case 'month':
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        start = getLocalDateString(monthAgo);
        endDateToSet = getLocalDateString(today); // End at today, not future
        setGroupBy('day');
        break;
      case 'year':
        const yearAgo = new Date();
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        start = getLocalDateString(yearAgo);
        endDateToSet = getLocalDateString(today); // End at today, not future
        setGroupBy('month');
        break;
      case 'all':
        // All time - set a very early date and extend into future
        start = '2020-01-01';
        const allEnd = new Date();
        allEnd.setFullYear(allEnd.getFullYear() + 2); // 2 years into future
        const allEndStr = getLocalDateString(allEnd);
        setGroupBy('month');
        setStartDate(start);
        setEndDate(allEndStr);
        return;
      case 'custom':
        // If dates are empty, set a default range
        if (!startDate || !endDate) {
          const defaultEnd = new Date();
          defaultEnd.setMonth(defaultEnd.getMonth() + 3);
          const defaultStart = new Date();
          defaultStart.setMonth(defaultStart.getMonth() - 3);
          setStartDate(getLocalDateString(defaultStart));
          setEndDate(getLocalDateString(defaultEnd));
        }
        return;
      default:
        const defaultStart = new Date();
        defaultStart.setMonth(defaultStart.getMonth() - 1);
        start = getLocalDateString(defaultStart);
    }

    setStartDate(start);
    setEndDate(endDateToSet);
  }, [period]);

  useEffect(() => {
    if (startDate && endDate) {
      loadStats();
    } else {
    }
  }, [startDate, endDate, groupBy]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/analytics/get_stats.php`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: startDate,
          end_date: endDate,
          group_by: groupBy,
        }
      });


      if (response.data.success) {
       
        setStats(response.data.data);
        setError('');
      } else {
        setError('Fehler beim Laden der Statistiken');
      }
    } catch (err) {
      setError('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = () => {
    if (!stats) return;

    const reportData = {
      period: stats.period,
      totals: stats.totals,
      timeSeries: stats.timeSeries,
      eventBreakdown: stats.eventBreakdown,
      topDates: stats.topDates,
      generatedAt: new Date().toISOString(),
    };

    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(reportData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `bericht_${startDate}_${endDate}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleDownloadCSV = () => {
    if (!stats || !stats.timeSeries) return;

    const csvHeader = 'Datum,Buchungen,Umsatz (CHF),Kinder\n';
    const csvRows = stats.timeSeries.map(row => 
      `${row.booking_date},${row.bookings},${parseFloat(row.revenue).toFixed(2)},${row.children}`
    ).join('\n');
    
    const csvContent = csvHeader + csvRows;
    const dataStr = "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent);
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `bericht_${startDate}_${endDate}.csv`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleDownloadPDF = () => {
    if (!stats) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(47, 163, 216); // Blue color
    doc.text("Goldfröschli's Kinderwelt", pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('Analyse Bericht', pageWidth / 2, 30, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Zeitraum: ${startDate} bis ${endDate}`, pageWidth / 2, 38, { align: 'center' });
    doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-CH')}`, pageWidth / 2, 44, { align: 'center' });
    
    // Summary Section
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Zusammenfassung', 14, 55);
    
    // Summary Table
    doc.autoTable({
      startY: 60,
      head: [['Metrik', 'Wert']],
      body: [
        ['Gesamtumsatz', `CHF ${parseFloat(stats.totals.total_revenue || 0).toFixed(2)}`],
        ['Durchschnitt pro Buchung', `CHF ${parseFloat(stats.totals.avg_booking_value || 0).toFixed(2)}`],
        ['Anzahl Buchungen', stats.totals.total_bookings || 0],
        ['Anzahl Kinder', stats.totals.total_children || 0],
        ['Durchschnitt Kinder/Buchung', stats.totals.total_bookings > 0 
          ? Math.round(stats.totals.total_children / stats.totals.total_bookings) 
          : 0],
      ],
      theme: 'striped',
      headStyles: { fillColor: [47, 163, 216] },
      margin: { left: 14, right: 14 },
    });
    
    // Event Breakdown Section
    if (stats.eventBreakdown && stats.eventBreakdown.length > 0) {
      const finalY1 = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(14);
      doc.text('Event Verteilung', 14, finalY1);
      
      doc.autoTable({
        startY: finalY1 + 5,
        head: [['Event', 'Buchungen', 'Umsatz (CHF)', 'Kinder']],
        body: stats.eventBreakdown.map(event => [
          event.event_name,
          event.bookings,
          `CHF ${parseFloat(event.revenue).toFixed(2)}`,
          event.children
        ]),
        theme: 'striped',
        headStyles: { fillColor: [184, 62, 49] },
        margin: { left: 14, right: 14 },
      });
    }
    
    // Time Series Data
    if (stats.timeSeries && stats.timeSeries.length > 0) {
      const finalY2 = doc.lastAutoTable.finalY + 10;
      
      // Check if we need a new page
      if (finalY2 > 250) {
        doc.addPage();
        doc.setFontSize(14);
        doc.text('Zeitverlauf', 14, 20);
        
        doc.autoTable({
          startY: 25,
          head: [['Datum', 'Buchungen', 'Umsatz (CHF)', 'Kinder']],
          body: stats.timeSeries.map(row => [
            row.booking_date,
            row.bookings,
            `CHF ${parseFloat(row.revenue).toFixed(2)}`,
            row.children
          ]),
          theme: 'striped',
          headStyles: { fillColor: [76, 175, 80] },
          margin: { left: 14, right: 14 },
        });
      } else {
        doc.setFontSize(14);
        doc.text('Zeitverlauf', 14, finalY2);
        
        doc.autoTable({
          startY: finalY2 + 5,
          head: [['Datum', 'Buchungen', 'Umsatz (CHF)', 'Kinder']],
          body: stats.timeSeries.map(row => [
            row.booking_date,
            row.bookings,
            `CHF ${parseFloat(row.revenue).toFixed(2)}`,
            row.children
          ]),
          theme: 'striped',
          headStyles: { fillColor: [76, 175, 80] },
          margin: { left: 14, right: 14 },
        });
      }
    }
    
    // Top Dates
    if (stats.topDates && stats.topDates.length > 0) {
      const finalY3 = doc.lastAutoTable.finalY + 10;
      
      // Check if we need a new page
      if (finalY3 > 250) {
        doc.addPage();
        doc.setFontSize(14);
        doc.text('Top 10 Tage (nach Umsatz)', 14, 20);
        
        doc.autoTable({
          startY: 25,
          head: [['Datum', 'Buchungen', 'Umsatz (CHF)', 'Kinder']],
          body: stats.topDates.map(date => [
            date.booking_date,
            date.bookings,
            `CHF ${parseFloat(date.revenue).toFixed(2)}`,
            date.children
          ]),
          theme: 'striped',
          headStyles: { fillColor: [255, 152, 0] },
          margin: { left: 14, right: 14 },
        });
      } else {
        doc.setFontSize(14);
        doc.text('Top 10 Tage (nach Umsatz)', 14, finalY3);
        
        doc.autoTable({
          startY: finalY3 + 5,
          head: [['Datum', 'Buchungen', 'Umsatz (CHF)', 'Kinder']],
          body: stats.topDates.map(date => [
            date.booking_date,
            date.bookings,
            `CHF ${parseFloat(date.revenue).toFixed(2)}`,
            date.children
          ]),
          theme: 'striped',
          headStyles: { fillColor: [255, 152, 0] },
          margin: { left: 14, right: 14 },
        });
      }
    }
    
    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Seite ${i} von ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }
    
    // Save the PDF
    doc.save(`analyse_bericht_${startDate}_${endDate}.pdf`);
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 900 }}>
        Analysen
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Zeitraum"
                value={period}
                onChange={(e) => {
                  setPeriod(e.target.value);
                }}
              >
                <MenuItem value="day">Heute</MenuItem>
                <MenuItem value="week">Letzte 7 Tage</MenuItem>
                <MenuItem value="month">Letzter Monat</MenuItem>
                <MenuItem value="year">Letztes Jahr</MenuItem>
                <MenuItem value="all">Alle Zeit</MenuItem>
                <MenuItem value="custom">Benutzerdefiniert</MenuItem>
              </TextField>
            </Grid>
            
            {period === 'custom' && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Von"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Bis"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Gruppierung"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <MenuItem value="day">Tag</MenuItem>
                <MenuItem value="week">Woche</MenuItem>
                <MenuItem value="month">Monat</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={period === 'custom' ? 1 : 2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCSV}
                disabled={!stats}
              >
                CSV
              </Button>
            </Grid>
            
            <Grid item xs={12} md={period === 'custom' ? 1 : 2}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleDownloadPDF}
                disabled={!stats}
              >
                PDF
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      

      {stats && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #2fa3d8 0%, #1976d2 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AttachMoneyIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle2">Gesamtumsatz</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    CHF {parseFloat(stats.totals.total_revenue || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption">
                    Ø CHF {parseFloat(stats.totals.avg_booking_value || 0).toFixed(2)} pro Buchung
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #b83e31 0%, #8a2f25 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EventIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle2">Buchungen</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {stats.totals.total_bookings || 0}
                  </Typography>
                  <Typography variant="caption">
                    Im gewählten Zeitraum
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle2">Kinder</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {stats.totals.total_children || 0}
                  </Typography>
                  <Typography variant="caption">
                    Gesamt Teilnehmer
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUpIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle2">Durchschnitt</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {stats.totals.total_bookings > 0 
                      ? Math.round(stats.totals.total_children / stats.totals.total_bookings)
                      : 0}
                  </Typography>
                  <Typography variant="caption">
                    Kinder pro Buchung
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Revenue Chart */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                    Umsatz-Verlauf
                  </Typography>
                  {stats.timeSeries && stats.timeSeries.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={stats.timeSeries.map(item => ({
                        ...item,
                        revenue: parseFloat(item.revenue) || 0,
                        bookings: parseInt(item.bookings) || 0,
                        children: parseInt(item.children) || 0
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="booking_date" />
                        <YAxis />
                        <Tooltip formatter={(value) => typeof value === 'number' ? value.toFixed(2) : value} />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#2fa3d8" name="Umsatz (CHF)" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                      <Typography variant="body2" color="text.secondary">
                        Keine Zeitreihen-Daten verfügbar
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                    Event-Verteilung
                  </Typography>
                  {stats.eventBreakdown && stats.eventBreakdown.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={stats.eventBreakdown.map(item => ({
                            ...item,
                            revenue: parseFloat(item.revenue) || 0
                          }))}
                          dataKey="revenue"
                          nameKey="event_name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={(entry) => `${entry.event_name}: CHF ${parseFloat(entry.revenue).toFixed(2)}`}
                        >
                          {stats.eventBreakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => `CHF ${parseFloat(value).toFixed(2)}`}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                      <Typography variant="body2" color="text.secondary">
                        Keine Event-Daten verfügbar
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Bookings and Children Chart */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                    Buchungen & Kinder
                  </Typography>
                  {stats.timeSeries && stats.timeSeries.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.timeSeries.map(item => ({
                        ...item,
                        bookings: parseInt(item.bookings) || 0,
                        children: parseInt(item.children) || 0
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="booking_date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="bookings" fill="#b83e31" name="Buchungen" />
                        <Bar dataKey="children" fill="#4caf50" name="Kinder" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                      <Typography variant="body2" color="text.secondary">
                        Keine Daten verfügbar
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Top Dates */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                Top 10 Tage (nach Umsatz)
              </Typography>
              <Grid container spacing={2}>
                {stats.topDates.map((date, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                          {date.booking_date}
                        </Typography>
                        <Typography variant="body2">
                          Umsatz: CHF {parseFloat(date.revenue).toFixed(2)}
                        </Typography>
                        <Typography variant="body2">
                          Buchungen: {date.bookings} | Kinder: {date.children}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default Analytics;
